import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Upload, RotateCcw, MapPin, Plus, Trash2, Shapes, Tag, Pencil, Route } from "lucide-react";
import { DEFAULT_PITCH_POSITIONS } from "@/lib/pitchMapDefaults";
import { AERIAL_PITCH_POSITIONS } from "@/lib/aerialPositions";
import { FEATURE_TYPES, FEATURE_ICONS, FEATURE_CHIP_COLORS, DEFAULT_FEATURES, BOUNDARY, lodgeShortName } from "@/lib/parkGeometry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBTYPE_COLORS = {
  hardstanding: "#3A322E",
  grass: "#6A8E3F",
  tent: "#3B8C4E",
  lodge: "#B76E79",
};

const LAYERS = [
  { key: "aerial", label: "Aerial photo", defaults: AERIAL_PITCH_POSITIONS },
  { key: "map", label: "Site plan", defaults: DEFAULT_PITCH_POSITIONS },
];

/**
 * Pitch map photos, pins & features editor — one editor per map layer (real
 * aerial photo / official site plan). Upload each photo, then drag every pin
 * onto its exact pitch. "Add pin" creates real bookable pitches AND lodges;
 * "Add feature" drops editable landmarks (trees, washroom, BBQ, villas,
 * fields…) that render on every layer AND in the 3D model; "Edit" renames any
 * pin or feature label; "Remove" deletes any pin or feature (units with active
 * bookings are protected server-side). Saved into site content
 * (`pitchmap.{map,aerial,features}`); the public Pick-Your-Pitch page uses
 * them immediately.
 */
const AdminPitchMap = () => {
  const [units, setUnits] = useState([]); // [{id, name, kind}]
  const [layer, setLayer] = useState("aerial");
  const [configs, setConfigs] = useState({ map: {}, aerial: {} }); // per-layer {photo_url, positions}
  const [positions, setPositions] = useState({});
  const [features, setFeatures] = useState(DEFAULT_FEATURES); // shared across layers + 3D
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [content, setContent] = useState(null);
  const [mode, setMode] = useState("idle"); // "idle" | "add" | "remove" | "feature" | "edit"
  const [spot, setSpot] = useState(null); // pending new-pin position {x, y}
  const [addForm, setAddForm] = useState({ prefix: "walnut-tent", number: "", name: "", nightly: "90" });
  const [adding, setAdding] = useState(false);
  const [featureType, setFeatureType] = useState("tree");
  const [featureDraft, setFeatureDraft] = useState(null); // pending feature position {x, y}
  const [featureLabel, setFeatureLabel] = useState("");
  const [editTarget, setEditTarget] = useState(null); // { kind: "pin"|"feature", id }
  const [editName, setEditName] = useState("");
  const [renaming, setRenaming] = useState(false);
  const dragRef = useRef(null); // { kind: "pin" | "feature" | "vertex", id/index }
  const areaRef = useRef(null);
  // ---- draw tools (boundary reshape + gravel paths) — plan frame = 3D frame ----
  const [geometry, setGeometry] = useState({ boundary: null, paths: [] });
  const [drawTool, setDrawTool] = useState("boundary"); // "boundary" | "path"
  const [boundaryDraft, setBoundaryDraft] = useState(null); // [[x,y],...] working copy
  const [selVertex, setSelVertex] = useState(null); // selected boundary point index
  const [pathDraft, setPathDraft] = useState([]); // [[x,y],...] path being drawn

  /** Next free guest-facing pitch number in a group (matches backend logic). */
  const nextNumber = (prefix) => {
    const taken = new Set();
    units.forEach((u) => {
      if (u.id.startsWith(prefix + "-")) {
        const idn = parseInt(u.id.split("-").pop(), 10);
        if (!isNaN(idn)) taken.add(prefix === "walnut-hs" ? idn - 10 : idn);
      }
    });
    let n = 1;
    while (taken.has(n)) n += 1;
    return n;
  };

  const defaults = layer === "aerial" ? AERIAL_PITCH_POSITIONS : DEFAULT_PITCH_POSITIONS;
  const photoUrl = configs[layer]?.photo_url || "";

  useEffect(() => {
    (async () => {
      try {
        const [{ data: unitsData }, { data: contentData }] = await Promise.all([
          axios.get(`${API}/units`),
          axios.get(`${API}/content`),
        ]);
        const list = [];
        for (const t of unitsData.types || []) {
          for (const u of t.units || []) {
            list.push({ id: u.id, name: u.name, kind: t.type === "lodge" ? "lodge" : u.sub_type, custom: !!u.custom });
          }
        }
        setUnits(list);
        setContent(contentData || {});
        const pm = contentData?.pitchmap || {};
        setConfigs({ map: pm.map || {}, aerial: pm.aerial || {} });
        setFeatures(Array.isArray(pm.features) ? pm.features : DEFAULT_FEATURES);
        setGeometry({
          boundary: Array.isArray(pm.geometry?.boundary) ? pm.geometry.boundary : null,
          paths: Array.isArray(pm.geometry?.paths) ? pm.geometry.paths : [],
        });
      } catch {
        toast.error("Couldn't load the pitch map data.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const switchMode = (m) => {
    const next = mode === m ? "idle" : m;
    setMode(next);
    setSpot(null);
    setFeatureDraft(null);
    setEditTarget(null);
    setSelVertex(null);
    setPathDraft([]);
    if (next === "draw") {
      // The 3D model's boundary/paths live in the SITE-PLAN frame — draw there.
      setLayer("map");
      setBoundaryDraft((geometry.boundary?.length >= 3 ? geometry.boundary : BOUNDARY).map((p) => [...p]));
    } else {
      setBoundaryDraft(null);
    }
  };

  // ---- add pins + drop features + draw ----
  const onAreaClick = (e) => {
    if (!areaRef.current) return;
    if (e.target.closest && (e.target.closest('[data-testid^="pin-"]') || e.target.closest('[data-testid^="map-feature-"]') || e.target.closest('[data-testid^="draw-"]'))) return; // clicked an existing marker/handle
    if (mode === "draw") {
      if (drawTool !== "path") return;
      const rect = areaRef.current.getBoundingClientRect();
      const x = Math.round(Math.min(99, Math.max(1, ((e.clientX - rect.left) / rect.width) * 100)) * 10) / 10;
      const y = Math.round(Math.min(99, Math.max(1, ((e.clientY - rect.top) / rect.height) * 100)) * 10) / 10;
      setPathDraft((prev) => [...prev, [x, y]]);
      return;
    }
    if (mode !== "add" && mode !== "feature") return;
    const rect = areaRef.current.getBoundingClientRect();
    const x = Math.round(Math.min(99, Math.max(1, ((e.clientX - rect.left) / rect.width) * 100)) * 10) / 10;
    const y = Math.round(Math.min(99, Math.max(1, ((e.clientY - rect.top) / rect.height) * 100)) * 10) / 10;
    if (mode === "add") {
      setSpot({ x, y });
      setAddForm((f) => ({ ...f, number: String(nextNumber(f.prefix)) }));
    } else {
      const meta = FEATURE_TYPES.find((t) => t.type === featureType);
      setFeatureLabel(meta?.defaultLabel || "");
      setFeatureDraft({ x, y });
    }
  };

  const addPitch = async () => {
    if (!spot) return;
    setAdding(true);
    try {
      const payload = { prefix: addForm.prefix };
      if (addForm.prefix === "lodge") {
        payload.name = addForm.name.trim();
        if (payload.name.length < 2) {
          toast.error("Give the lodge a name — e.g. 'Willow Lodge'.");
          setAdding(false);
          return;
        }
        const price = parseFloat(addForm.nightly);
        if (price > 0) payload.nightly = price;
      } else if (addForm.number !== "") {
        payload.number = parseInt(addForm.number, 10);
      }
      const { data } = await axios.post(`${API}/admin/pitches/add`, payload);
      const u = data.unit;
      const newPositions = { ...positions, [u.id]: spot };
      setPositions(newPositions);
      setUnits((prev) => [...prev, { id: u.id, name: u.name, kind: u.sub_type || "lodge", custom: true }]);
      await save(newPositions);
      toast.success(`Added ${u.name} — it's bookable on the website right away.`);
      setSpot(null);
      setMode("idle");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add the pitch.");
    } finally {
      setAdding(false);
    }
  };

  // ---- remove ANY pin (built-ins guarded server-side by active bookings) ----
  const removePitch = async (u) => {
    const warn = u.custom
      ? `Remove ${u.name}? This deletes it from the map and the booking system.`
      : `Remove ${u.name}? This is a BUILT-IN pitch/lodge — removing it takes it off the map AND out of the booking system (a unit with active bookings can't be removed). This can't be undone from here.`;
    if (!window.confirm(warn)) return;
    try {
      await axios.delete(`${API}/admin/pitches/${u.id}`);
      setUnits((prev) => prev.filter((x) => x.id !== u.id));
      setPositions((prev) => {
        const next = { ...prev };
        delete next[u.id];
        return next;
      });
      toast.success(`${u.name} removed.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove that pin.");
    }
  };

  // ---- edit (rename) any pin or feature label ----
  const openEdit = (target, currentName) => {
    setEditTarget(target);
    setEditName(currentName || "");
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    const name = editName.trim();
    const isTree = editTarget.kind === "feature" && features.find((f) => f.id === editTarget.id)?.type === "tree";
    if (!isTree && name.length < 2) {
      toast.error("Name must be at least 2 characters.");
      return;
    }
    setRenaming(true);
    try {
      if (editTarget.kind === "pin") {
        const { data } = await axios.put(`${API}/admin/units/${editTarget.id}/rename`, { name });
        setUnits((prev) => prev.map((x) => (x.id === editTarget.id ? { ...x, name: data.name } : x)));
        toast.success(`Renamed to "${data.name}" — live everywhere now.`);
      } else {
        const nextFeats = features.map((f) => (f.id === editTarget.id ? { ...f, label: name } : f));
        setFeatures(nextFeats);
        await save(null, nextFeats);
        toast.success("Label updated — live on the map.");
      }
      setEditTarget(null);
      setMode("idle");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the name.");
    } finally {
      setRenaming(false);
    }
  };

  // ---- map features (trees, washroom, BBQ…) — shared by all layers + 3D ----
  const confirmFeature = async () => {
    if (!featureDraft) return;
    const meta = FEATURE_TYPES.find((t) => t.type === featureType);
    const f = {
      id: `feat-${Date.now().toString(36)}`,
      type: featureType,
      label: featureLabel.trim() || meta?.defaultLabel || "",
      x: featureDraft.x,
      y: featureDraft.y,
    };
    if (f.type === "tree" || f.type === "grass") f.label = featureLabel.trim();
    const nextFeats = [...features, f];
    setFeatures(nextFeats);
    setFeatureDraft(null);
    setMode("idle");
    await save(null, nextFeats);
    toast.success(`${f.label || meta?.label || "Feature"} added — live on every map layer and in the 3D model.`);
  };

  const removeFeature = async (f) => {
    if (!window.confirm(`Remove "${f.label || "this tree"}" from the map and the 3D model?`)) return;
    const nextFeats = features.filter((x) => x.id !== f.id);
    setFeatures(nextFeats);
    await save(null, nextFeats);
    toast.success("Removed — live on the website now.");
  };

  // ---- draw tools: reshape the park boundary, lay gravel paths/entrances ----
  const saveGeometry = async (geo) => {
    setGeometry(geo);
    await save(null, null, geo);
  };

  const onVertexDown = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { kind: "vertex", index: i };
    setSelVertex(i);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const insertVertex = (i) => {
    setBoundaryDraft((prev) => {
      if (!prev) return prev;
      const a = prev[i];
      const b = prev[(i + 1) % prev.length];
      const mid = [Math.round(((a[0] + b[0]) / 2) * 10) / 10, Math.round(((a[1] + b[1]) / 2) * 10) / 10];
      const next = [...prev];
      next.splice(i + 1, 0, mid);
      return next;
    });
    setSelVertex(i + 1);
  };

  const deleteVertex = () => {
    if (selVertex == null || !boundaryDraft) return;
    if (boundaryDraft.length <= 3) { toast.error("A boundary needs at least 3 points."); return; }
    setBoundaryDraft(boundaryDraft.filter((_, i) => i !== selVertex));
    setSelVertex(null);
  };

  const saveBoundary = async () => {
    if (!boundaryDraft || boundaryDraft.length < 3) { toast.error("A boundary needs at least 3 points."); return; }
    await saveGeometry({ boundary: boundaryDraft, paths: geometry?.paths || [] });
    toast.success("Park boundary updated — the 3D model's grass and hedge now follow your line.");
  };

  const resetBoundary = async () => {
    if (!window.confirm("Go back to the original traced boundary? Your redrawn line will be lost.")) return;
    await saveGeometry({ boundary: null, paths: geometry?.paths || [] });
    setBoundaryDraft(BOUNDARY.map((p) => [...p]));
    setSelVertex(null);
    toast.success("Boundary back to the original — live in the 3D model.");
  };

  const finishPath = async () => {
    if (pathDraft.length < 2) { toast.error("Tap at least two points to make a path."); return; }
    const paths = [...(geometry?.paths || []), { id: `path-${Date.now().toString(36)}`, pts: pathDraft }];
    setPathDraft([]);
    await saveGeometry({ boundary: geometry?.boundary || null, paths });
    toast.success("Path saved — it renders as gravel in the 3D model.");
  };

  const removePath = async (id) => {
    if (!window.confirm("Remove this drawn path from the 3D model?")) return;
    const paths = (geometry?.paths || []).filter((p) => p.id !== id);
    await saveGeometry({ boundary: geometry?.boundary || null, paths });
    toast.success("Path removed.");
  };

  // active layer's saved positions over that layer's defaults
  useEffect(() => {
    setPositions({ ...defaults, ...(configs[layer]?.positions || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, configs]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setConfigs((prev) => ({ ...prev, [layer]: { ...(prev[layer] || {}), photo_url: url } }));
      toast.success("Photo uploaded — now press Save map.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onPinDown = (e, id) => {
    if (mode !== "idle") return; // in add/remove/feature/edit mode pins don't drag
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { kind: "pin", id };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onFeatureDown = (e, id) => {
    if (mode !== "idle") return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { kind: "feature", id };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onAreaMove = (e) => {
    const drag = dragRef.current;
    if (!drag || !areaRef.current) return;
    const rect = areaRef.current.getBoundingClientRect();
    const x = Math.round(Math.min(99, Math.max(1, ((e.clientX - rect.left) / rect.width) * 100)) * 10) / 10;
    const y = Math.round(Math.min(99, Math.max(1, ((e.clientY - rect.top) / rect.height) * 100)) * 10) / 10;
    if (drag.kind === "vertex") {
      setBoundaryDraft((prev) => (prev ? prev.map((p, i) => (i === drag.index ? [x, y] : p)) : prev));
    } else if (drag.kind === "feature") {
      setFeatures((prev) => prev.map((f) => (f.id === drag.id ? { ...f, x, y } : f)));
    } else {
      setPositions((prev) => ({ ...prev, [drag.id]: { x, y } }));
    }
  };

  const onAreaUp = () => {
    dragRef.current = null;
  };

  const save = async (positionsOverride, featuresOverride, geometryOverride) => {
    setSaving(true);
    try {
      const posToSave = positionsOverride || positions;
      const featsToSave = featuresOverride || features;
      const geoToSave = geometryOverride || geometry;
      const nextLayer = { photo_url: photoUrl, positions: posToSave };
      const nextConfigs = { ...configs, [layer]: nextLayer };
      const next = {
        ...(content || {}),
        pitchmap: {
          ...(content?.pitchmap || {}),
          map: nextConfigs.map || {},
          aerial: nextConfigs.aerial || {},
          features: featsToSave,
          geometry: geoToSave,
        },
      };
      await axios.put(`${API}/admin/content`, next);
      setContent(next);
      setConfigs(nextConfigs);
      toast.success("Pitch map saved — live on the website now.");
    } catch {
      toast.error("Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (!window.confirm("Reset every pin on this layer to the default positions? Your drags will be lost.")) return;
    setPositions({ ...defaults });
    toast.success("Pins reset to defaults — drag them to the right spots and save.");
  };

  const dirty = useMemo(() => {
    const saved = configs[layer]?.positions || {};
    const pinsDirty = JSON.stringify(positions) !== JSON.stringify({ ...defaults, ...saved });
    const savedFeats = Array.isArray(content?.pitchmap?.features) ? content.pitchmap.features : DEFAULT_FEATURES;
    const featsDirty = JSON.stringify(features) !== JSON.stringify(savedFeats);
    const normGeo = (g) => JSON.stringify({ boundary: g?.boundary || null, paths: g?.paths || [] });
    const geoDirty = normGeo(geometry) !== normGeo(content?.pitchmap?.geometry);
    return pinsDirty || featsDirty || geoDirty;
  }, [positions, configs, layer, defaults, features, content, geometry]);

  if (loading) {
    return (
      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-8 flex items-center gap-2 text-[#6A5F58] text-sm" data-testid="pitchmap-editor-loading">
        <Loader2 size={15} className="animate-spin" /> Loading pitch map editor…
      </div>
    );
  }

  const PendingFeatureIcon = FEATURE_ICONS[featureType] || Tag;
  const editIsTree = editTarget?.kind === "feature" && features.find((f) => f.id === editTarget.id)?.type === "tree";

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-5" data-testid="pitchmap-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-serif text-lg text-[#14100F]">Pitch map photos, pins &amp; features</div>
          <p className="text-xs text-[#6A5F58] mt-1 max-w-xl">
            Two map layers: the <strong>real aerial photo</strong> (hyper-real view) and the <strong>official site plan</strong>.
            Drag pins to move them, <strong>Edit</strong> to rename any pin, <strong>Add pin</strong> for real bookable pitches &amp; lodges,
            and <strong>Add feature</strong> for trees, washroom, BBQ, villas, fields and more — features show on both layers and in the 3D model.
          </p>
          <div className="mt-3 inline-flex rounded-full border border-[#DDD1BE] overflow-hidden" data-testid="pitchmap-layer-toggle">
            {LAYERS.map((l) => (
              <button
                key={l.key}
                type="button"
                onClick={() => setLayer(l.key)}
                className={`px-4 h-9 text-[10px] font-brand tracking-[0.12em] uppercase transition ${layer === l.key ? "bg-[#0F0E0E] text-[#F5EFE6]" : "bg-white text-[#6A5F58] hover:bg-[#F5EFE6]"}`}
                data-testid={`pitchmap-layer-${l.key}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase transition cursor-pointer" data-testid="pitchmap-photo-upload">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {photoUrl ? "Replace photo" : "Upload photo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          </label>
          <button type="button" onClick={resetDefaults} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-[#DDD1BE] text-[#6A5F58] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase transition" data-testid="pitchmap-reset">
            <RotateCcw size={12} /> Reset pins
          </button>
          <button
            type="button"
            onClick={() => switchMode("add")}
            className={`inline-flex items-center gap-1.5 px-4 h-10 rounded-full border text-[10px] font-brand tracking-[0.12em] uppercase transition ${mode === "add" ? "bg-[#3B8C4E] border-[#3B8C4E] text-white" : "border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6]"}`}
            data-testid="pitchmap-add-toggle"
          >
            <Plus size={13} /> Add pin
          </button>
          <button
            type="button"
            onClick={() => switchMode("feature")}
            className={`inline-flex items-center gap-1.5 px-4 h-10 rounded-full border text-[10px] font-brand tracking-[0.12em] uppercase transition ${mode === "feature" ? "bg-[#0F0E0E] border-[#0F0E0E] text-[#F5EFE6]" : "border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6]"}`}
            data-testid="pitchmap-feature-toggle"
          >
            <Shapes size={13} /> Add feature
          </button>
          <button
            type="button"
            onClick={() => switchMode("edit")}
            className={`inline-flex items-center gap-1.5 px-4 h-10 rounded-full border text-[10px] font-brand tracking-[0.12em] uppercase transition ${mode === "edit" ? "bg-[#33546D] border-[#33546D] text-white" : "border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6]"}`}
            data-testid="pitchmap-edit-toggle"
          >
            <Pencil size={13} /> Edit
          </button>
          <button
            type="button"
            onClick={() => switchMode("draw")}
            className={`inline-flex items-center gap-1.5 px-4 h-10 rounded-full border text-[10px] font-brand tracking-[0.12em] uppercase transition ${mode === "draw" ? "bg-[#6A5F58] border-[#6A5F58] text-white" : "border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6]"}`}
            data-testid="pitchmap-draw-toggle"
          >
            <Route size={13} /> Draw
          </button>
          <button
            type="button"
            onClick={() => switchMode("remove")}
            className={`inline-flex items-center gap-1.5 px-4 h-10 rounded-full border text-[10px] font-brand tracking-[0.12em] uppercase transition ${mode === "remove" ? "bg-[#B76E79] border-[#B76E79] text-white" : "border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6]"}`}
            data-testid="pitchmap-remove-toggle"
          >
            <Trash2 size={13} /> Remove
          </button>
          <button type="button" onClick={() => save()} disabled={saving} className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-50" data-testid="pitchmap-save">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save map
          </button>
        </div>
      </div>

      {!photoUrl ? (
        <div className="mt-4 border-2 border-dashed border-[#DDD1BE] rounded-xl p-10 text-center" data-testid="pitchmap-no-photo">
          <MapPin size={26} className="mx-auto text-[#DDD1BE]" />
          <p className="mt-3 text-sm text-[#6A5F58]">
            No {layer === "aerial" ? "aerial photo" : "site plan"} yet — upload one above. Until then the website falls back to the other layer.
          </p>
        </div>
      ) : (
        <>
        {mode === "add" && !spot && (
          <div className="mt-4 rounded-xl bg-[#3B8C4E]/10 border border-[#3B8C4E]/30 px-4 py-3 text-xs text-[#2E6B3C]" data-testid="pitchmap-add-hint">
            Tap the photo exactly where the new pitch or lodge sits — then choose its group and number.
          </div>
        )}
        {mode === "edit" && !editTarget && (
          <div className="mt-4 rounded-xl bg-[#33546D]/10 border border-[#33546D]/30 px-4 py-3 text-xs text-[#33546D]" data-testid="pitchmap-edit-hint">
            Tap any pin or feature chip to rename it — the new name shows on the website, quotes and emails.
          </div>
        )}
        {mode === "remove" && (
          <div className="mt-4 rounded-xl bg-[#B76E79]/10 border border-[#B76E79]/30 px-4 py-3 text-xs text-[#8A4A55]" data-testid="pitchmap-remove-hint">
            Tap <strong>any pin or feature chip</strong> to remove it — including trees and built-in pitches.
            A pitch or lodge with active bookings can't be removed until those bookings are cancelled or moved.
          </div>
        )}
        {mode === "feature" && !featureDraft && (
          <div className="mt-4 rounded-xl bg-[#F5EFE6] border border-[#DDD1BE] px-4 py-3" data-testid="feature-palette">
            <p className="text-[10px] uppercase tracking-[0.14em] text-[#6A5F58] mb-2">
              Pick what to drop, then tap the map — features appear on both layers and in the 3D model
            </p>
            <div className="flex flex-wrap gap-2">
              {FEATURE_TYPES.map((t) => {
                const Icon = FEATURE_ICONS[t.type] || Tag;
                return (
                  <button
                    key={t.type}
                    type="button"
                    onClick={() => setFeatureType(t.type)}
                    className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full border text-[10px] font-brand tracking-[0.1em] uppercase transition ${featureType === t.type ? "bg-[#0F0E0E] text-[#F5EFE6] border-[#0F0E0E]" : "bg-white border-[#DDD1BE] text-[#3A322E] hover:bg-[#EDE3D2]"}`}
                    data-testid={`feature-type-${t.type}`}
                  >
                    <Icon size={12} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {editTarget && (
          <div className="mt-4 border border-[#33546D]/40 bg-[#F3F7FB] rounded-xl p-4 flex flex-wrap items-end gap-4" data-testid="pin-edit-panel">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.14em] text-[#6A5F58] mb-1">
                {editTarget.kind === "pin" ? "Pin name" : "Feature label"}
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                disabled={editIsTree}
                placeholder={editIsTree ? "Trees have no label" : ""}
                className="h-10 w-64 px-3 rounded-lg border border-[#DDD1BE] bg-white text-sm text-[#14100F] disabled:opacity-50"
                data-testid="pin-edit-name"
              />
              {editTarget.kind === "pin" && (
                <p className="text-[10px] text-[#6A5F58] mt-1">Renames it everywhere — website, quotes, bookings &amp; emails</p>
              )}
            </div>
            {!editIsTree && (
              <button
                type="button"
                onClick={saveEdit}
                disabled={renaming}
                className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-[#33546D] text-white hover:bg-[#0F0E0E] text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-50"
                data-testid="pin-edit-save"
              >
                {renaming ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save name
              </button>
            )}
            <button type="button" onClick={() => { setEditTarget(null); setMode("idle"); }} className="text-[10px] uppercase tracking-[0.12em] text-[#6A5F58] hover:text-[#B76E79]" data-testid="pin-edit-cancel">
              Done
            </button>
          </div>
        )}
        {spot && (
          <div className="mt-4 border border-[#B76E79]/40 bg-[#FFF8F6] rounded-xl p-4 flex flex-wrap items-end gap-4" data-testid="pitchmap-add-panel">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.14em] text-[#6A5F58] mb-1">Pitch group</label>
              <select
                value={addForm.prefix}
                onChange={(e) => setAddForm((f) => ({ ...f, prefix: e.target.value, number: String(nextNumber(e.target.value)) }))}
                className="h-10 px-3 rounded-lg border border-[#DDD1BE] bg-white text-sm text-[#14100F]"
                data-testid="pitchmap-add-group"
              >
                <option value="daisy-hs">Daisy Lake — hardstanding (£30/nt)</option>
                <option value="walnut-hs">Walnut Lake — hardstanding (£30/nt)</option>
                <option value="walnut-grass">Grass with electric (£30/nt)</option>
                <option value="walnut-tent">Tent — no electric (£24/nt)</option>
                <option value="lodge">New lodge — real bookable unit</option>
              </select>
            </div>
            {addForm.prefix === "lodge" ? (
              <>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.14em] text-[#6A5F58] mb-1">Lodge name</label>
                  <input
                    type="text"
                    value={addForm.name}
                    onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Willow Lodge"
                    className="h-10 w-48 px-3 rounded-lg border border-[#DDD1BE] bg-white text-sm text-[#14100F]"
                    data-testid="pitchmap-add-lodge-name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.14em] text-[#6A5F58] mb-1">Nightly price (£)</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={addForm.nightly}
                    onChange={(e) => setAddForm((f) => ({ ...f, nightly: e.target.value }))}
                    className="h-10 w-24 px-3 rounded-lg border border-[#DDD1BE] bg-white text-sm text-[#14100F]"
                    data-testid="pitchmap-add-lodge-nightly"
                  />
                  <p className="text-[10px] text-[#6A5F58] mt-1">Defaults to £90 if left blank</p>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-[10px] uppercase tracking-[0.14em] text-[#6A5F58] mb-1">Pitch number</label>
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={addForm.number}
                  onChange={(e) => setAddForm((f) => ({ ...f, number: e.target.value }))}
                  className="h-10 w-24 px-3 rounded-lg border border-[#DDD1BE] bg-white text-sm text-[#14100F]"
                  data-testid="pitchmap-add-number"
                />
                <p className="text-[10px] text-[#6A5F58] mt-1">Next free is {nextNumber(addForm.prefix)} — or type your own</p>
              </div>
            )}
            <button
              type="button"
              onClick={addPitch}
              disabled={adding}
              className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-50"
              data-testid="pitchmap-add-confirm"
            >
              {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {addForm.prefix === "lodge" ? "Add lodge here" : "Add pitch here"}
            </button>
            <button type="button" onClick={() => { setSpot(null); setMode("idle"); }} className="text-[10px] uppercase tracking-[0.12em] text-[#6A5F58] hover:text-[#B76E79]" data-testid="pitchmap-add-cancel">
              Cancel
            </button>
          </div>
        )}
        {featureDraft && (
          <div className="mt-4 border border-[#3B8C4E]/40 bg-[#F4FAF2] rounded-xl p-4 flex flex-wrap items-end gap-4" data-testid="feature-draft-panel">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.14em] text-[#6A5F58] mb-1">
                Label — {FEATURE_TYPES.find((t) => t.type === featureType)?.label}
              </label>
              <input
                type="text"
                value={featureLabel}
                onChange={(e) => setFeatureLabel(e.target.value)}
                placeholder={featureType === "tree" ? "Trees have no label" : "e.g. Washroom"}
                disabled={featureType === "tree"}
                className="h-10 w-52 px-3 rounded-lg border border-[#DDD1BE] bg-white text-sm text-[#14100F] disabled:opacity-50"
                data-testid="feature-draft-label"
              />
              <p className="text-[10px] text-[#6A5F58] mt-1">Shown on the map and floating in the 3D model</p>
            </div>
            <button
              type="button"
              onClick={confirmFeature}
              className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-[#3B8C4E] text-white hover:bg-[#2E6B3C] text-[10px] font-brand tracking-[0.12em] uppercase transition"
              data-testid="feature-draft-confirm"
            >
              <Plus size={13} /> Drop it here
            </button>
            <button type="button" onClick={() => { setFeatureDraft(null); setMode("idle"); }} className="text-[10px] uppercase tracking-[0.12em] text-[#6A5F58] hover:text-[#B76E79]" data-testid="feature-draft-cancel">
              Cancel
            </button>
          </div>
        )}
        {mode === "draw" && boundaryDraft && (
          <div className="mt-4 border border-[#6A5F58]/30 bg-[#F7F4EE] rounded-xl p-4" data-testid="draw-panel">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setDrawTool("boundary")}
                className={`px-4 h-9 rounded-full border text-[10px] font-brand tracking-[0.12em] uppercase transition ${drawTool === "boundary" ? "bg-[#3B8C4E] border-[#3B8C4E] text-white" : "bg-white border-[#DDD1BE] text-[#3A322E] hover:bg-[#EDE3D2]"}`}
                data-testid="draw-tool-boundary"
              >
                Park boundary
              </button>
              <button
                type="button"
                onClick={() => setDrawTool("path")}
                className={`px-4 h-9 rounded-full border text-[10px] font-brand tracking-[0.12em] uppercase transition ${drawTool === "path" ? "bg-[#8A7A5C] border-[#8A7A5C] text-white" : "bg-white border-[#DDD1BE] text-[#3A322E] hover:bg-[#EDE3D2]"}`}
                data-testid="draw-tool-path"
              >
                Path / entrance
              </button>
              <span className="text-[10px] text-[#6A5F58] uppercase tracking-[0.1em]">Draws on the site plan — the 3D model follows exactly</span>
            </div>

            {drawTool === "boundary" ? (
              <div className="mt-3">
                <p className="text-xs text-[#3A322E] leading-relaxed">
                  Drag the <strong>white handles</strong> to reshape the green park edge — the grass AND the dark hedge both follow it in the 3D model.
                  Tap the small <strong>+</strong> on a line to add a handle; tap a handle to select it, then delete it below.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={saveBoundary}
                    className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-[#3B8C4E] text-white hover:bg-[#2E6B3C] text-[10px] font-brand tracking-[0.12em] uppercase transition"
                    data-testid="draw-save-boundary"
                  >
                    <Save size={13} /> Save boundary
                  </button>
                  <button
                    type="button"
                    onClick={deleteVertex}
                    disabled={selVertex == null}
                    className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-40"
                    data-testid="draw-delete-vertex"
                  >
                    <Trash2 size={12} /> Delete selected point
                  </button>
                  <button
                    type="button"
                    onClick={resetBoundary}
                    className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-[#DDD1BE] text-[#6A5F58] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase transition"
                    data-testid="draw-reset-boundary"
                  >
                    <RotateCcw size={12} /> Back to original
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-3">
                <p className="text-xs text-[#3A322E] leading-relaxed">
                  Tap along the route to lay a <strong>gravel path or new entrance</strong> — at least two points.
                  It renders in the 3D model straight away.
                  {pathDraft.length > 0 && <strong> Drawing: {pathDraft.length} point{pathDraft.length > 1 ? "s" : ""} down.</strong>}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={finishPath}
                    disabled={pathDraft.length < 2}
                    className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-[#8A7A5C] text-white hover:bg-[#0F0E0E] text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-40"
                    data-testid="draw-save-path"
                  >
                    <Save size={13} /> Save path
                  </button>
                  <button
                    type="button"
                    onClick={() => setPathDraft((prev) => prev.slice(0, -1))}
                    disabled={pathDraft.length === 0}
                    className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-[#DDD1BE] text-[#6A5F58] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-40"
                    data-testid="draw-undo-point"
                  >
                    Undo point
                  </button>
                  <button
                    type="button"
                    onClick={() => setPathDraft([])}
                    disabled={pathDraft.length === 0}
                    className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-[#DDD1BE] text-[#6A5F58] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-40"
                    data-testid="draw-clear-path"
                  >
                    Clear
                  </button>
                </div>
                {(geometry?.paths || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="draw-path-list">
                    {geometry.paths.map((p, i) => (
                      <span key={p.id} className="inline-flex items-center gap-2 px-3 h-8 rounded-full bg-white border border-[#DDD1BE] text-[10px] uppercase tracking-[0.1em] text-[#3A322E]">
                        Path {i + 1} · {p.pts.length} pts
                        <button type="button" onClick={() => removePath(p.id)} className="text-[#B76E79] hover:text-[#8f4a52]" data-testid={`draw-remove-path-${p.id}`} title="Remove path">
                          <Trash2 size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div
          ref={areaRef}
          className={`mt-4 relative rounded-xl overflow-hidden border border-[#DDD1BE] select-none touch-none ${mode === "add" || mode === "feature" || (mode === "draw" && drawTool === "path") ? "cursor-crosshair" : ""}`}
          onPointerMove={onAreaMove}
          onPointerUp={onAreaUp}
          onPointerLeave={onAreaUp}
          onClick={onAreaClick}
          data-testid="pitchmap-editor-area"
        >
          <img src={photoUrl} alt="Pitch map layer" className="w-full h-auto block" draggable={false} />
          {spot && (
            <span
              className="absolute w-7 h-7 -ml-3.5 -mt-3.5 rounded-full bg-[#3B8C4E] text-white text-[11px] font-bold shadow-lg flex items-center justify-center ring-2 ring-white animate-pulse pointer-events-none"
              style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
              data-testid="pin-pending"
            >
              +
            </span>
          )}
          {featureDraft && (
            <span
              className="absolute w-6 h-6 -ml-3 -mt-3 rounded-md text-white shadow-lg flex items-center justify-center ring-2 ring-white animate-pulse pointer-events-none"
              style={{ left: `${featureDraft.x}%`, top: `${featureDraft.y}%`, background: FEATURE_CHIP_COLORS[featureType] || "#0F0E0E" }}
              data-testid="map-feature-pending"
            >
              <PendingFeatureIcon size={11} />
            </span>
          )}
          {units.map((u) => {
            const pos = positions[u.id];
            if (!pos) return null;
            const m = /Pitch (\d+)/.exec(u.name || "");
            const n = m ? m[1] : u.id.split("-").pop().replace(/^0/, "");
            const isLodgePin = u.kind === "lodge";
            const label = isLodgePin ? lodgeShortName(u.name) : n;
            return (
              <button
                key={u.id}
                type="button"
                onPointerDown={(e) => onPinDown(e, u.id)}
                onClick={(e) => {
                  if (mode === "remove") {
                    e.stopPropagation();
                    removePitch(u);
                  } else if (mode === "edit") {
                    e.stopPropagation();
                    openEdit({ kind: "pin", id: u.id }, u.name);
                  }
                }}
                className={`absolute ${
                  isLodgePin ? "h-7 px-2.5 -mt-3.5 -translate-x-1/2 text-[10px] whitespace-nowrap" : "w-7 h-7 -ml-3.5 -mt-3.5 text-[11px]"
                } rounded-full text-white font-bold shadow-lg flex items-center justify-center transition-all ${
                  mode === "idle" ? "cursor-grab active:cursor-grabbing hover:scale-110" : "cursor-pointer"
                } ${u.custom ? "ring-2 ring-[#B76E79]" : ""} ${mode === "remove" ? "animate-pulse scale-110" : ""} ${mode === "edit" ? "ring-2 ring-[#33546D]" : ""}`}
                style={{ left: `${pos.x}%`, top: `${pos.y}%`, background: SUBTYPE_COLORS[u.kind] || "#3A322E" }}
                title={`${u.name} — drag to move${mode === "remove" ? ", tap to remove" : mode === "edit" ? ", tap to rename" : ""}`}
                data-testid={`pin-${u.id}`}
              >
                {label}
              </button>
            );
          })}
          {features.map((f) => {
            const Icon = FEATURE_ICONS[f.type] || Tag;
            return (
              <button
                key={f.id}
                type="button"
                onPointerDown={(e) => onFeatureDown(e, f.id)}
                onClick={(e) => {
                  if (mode === "remove") {
                    e.stopPropagation();
                    removeFeature(f);
                  } else if (mode === "edit") {
                    e.stopPropagation();
                    openEdit({ kind: "feature", id: f.id }, f.label || "");
                  }
                }}
                className={`absolute w-6 h-6 -ml-3 -mt-3 rounded-md text-white shadow-md flex items-center justify-center transition-all ${
                  mode === "idle" ? "cursor-grab active:cursor-grabbing hover:scale-110" : "cursor-pointer"
                } ${mode === "remove" ? "animate-pulse scale-110 ring-2 ring-[#B76E79]" : ""} ${mode === "edit" ? "ring-2 ring-[#33546D]" : ""}`}
                style={{ left: `${f.x}%`, top: `${f.y}%`, background: FEATURE_CHIP_COLORS[f.type] || "#0F0E0E" }}
                title={`${f.label || FEATURE_TYPES.find((t) => t.type === f.type)?.label || "Feature"} — drag to move${mode === "remove" ? ", tap to remove" : mode === "edit" ? ", tap to rename" : ""}`}
                data-testid={`map-feature-${f.id}`}
              >
                <Icon size={11} />
              </button>
            );
          })}
          {mode === "draw" && boundaryDraft && drawTool === "boundary" && (
            <>
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="draw-boundary-svg">
                <polygon
                  points={boundaryDraft.map((p) => p.join(",")).join(" ")}
                  fill="rgba(59,140,78,0.18)"
                  stroke="#3B8C4E"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
              {boundaryDraft.map((p, i) => {
                const b = boundaryDraft[(i + 1) % boundaryDraft.length];
                const mid = [(p[0] + b[0]) / 2, (p[1] + b[1]) / 2];
                return (
                  <React.Fragment key={i}>
                    <button
                      type="button"
                      onPointerDown={(e) => onVertexDown(e, i)}
                      className={`absolute w-4 h-4 -ml-2 -mt-2 rounded-full bg-white border-2 border-[#3B8C4E] shadow cursor-grab active:cursor-grabbing z-10 transition-transform ${selVertex === i ? "ring-2 ring-[#B76E79] scale-125" : "hover:scale-125"}`}
                      style={{ left: `${p[0]}%`, top: `${p[1]}%` }}
                      title={`Point ${i + 1} — drag to move, tap to select`}
                      data-testid={`draw-vertex-${i}`}
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); insertVertex(i); }}
                      className="absolute w-4 h-4 -ml-2 -mt-2 rounded-full bg-white border border-[#3B8C4E] text-[#3B8C4E] text-[10px] font-bold flex items-center justify-center hover:scale-125 z-10"
                      style={{ left: `${mid[0]}%`, top: `${mid[1]}%` }}
                      title="Add a point here"
                      data-testid={`draw-edge-plus-${i}`}
                    >
                      +
                    </button>
                  </React.Fragment>
                );
              })}
            </>
          )}
          {mode === "draw" && drawTool === "path" && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="draw-path-svg">
              {(geometry?.paths || []).map((p) => (
                <polyline
                  key={p.id}
                  points={p.pts.map((q) => q.join(",")).join(" ")}
                  fill="none"
                  stroke="#8A7A5C"
                  strokeWidth={3}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {pathDraft.length > 0 && (
                <polyline
                  points={pathDraft.map((q) => q.join(",")).join(" ")}
                  fill="none"
                  stroke="#B76E79"
                  strokeWidth={2.5}
                  strokeDasharray="4 3"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </svg>
          )}
        </div>
        </>
      )}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.12em] text-[#6A5F58]">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: SUBTYPE_COLORS.hardstanding }} /> Hardstanding</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: SUBTYPE_COLORS.grass }} /> Grass + EHU</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: SUBTYPE_COLORS.tent }} /> Tent</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: SUBTYPE_COLORS.lodge }} /> Lodge</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-md" style={{ background: "#0F0E0E" }} /> Feature chips = trees &amp; amenities (all layers + 3D)</span>
        {dirty && <span className="text-[#B76E79] font-semibold">Unsaved changes — press Save map</span>}
      </div>
    </div>
  );
};

export default AdminPitchMap;
